import get from 'lodash/get';
import format from 'date-fns-old/format';
import dateFnsAddHours from 'date-fns-old/add_hours';
import isSameDay from 'date-fns-old/is_same_day';
import isSameYear from 'date-fns-old/is_same_year';
import moment from 'moment';
import momentTz from 'moment-timezone';
import validateDate from '../validateDate';
import { DEFAULT_LOCALE } from '../../i18n';

import {
  DATE_PATTERN_API,
  DATE_PATTERN_UI,
  DAY_MONTH_FORMAT,
  SHORT_DATE_PATTERN,
  TIME_PATTERN,
  FULL_SHORT_DATE_PATTERN,
} from '../../constants/strings';

function splitUIDate(string, separator = '-') {
  const filterRegexIEBug = /[^ -~]/g;
  const parts = string
    .split(separator)
    .map(item => item.replace(filterRegexIEBug, ''));

  const day = parseInt(parts[1], 10);
  const month = parseInt(parts[0], 10);
  const year = parseInt(parts[2], 10);

  return { year, month, day };
}

function createDate({ year, month, day }) {
  // validate text input [UI]
  if (!validateDate(year, month, day)) return null;

  const date = new Date();
  // allow 0-99 year range
  date.setFullYear(year);
  date.setMonth(month - 1);
  date.setDate(day);

  return date;
}

// assume we have dateString mm-dd-yyyy. TODO: maybe switch to default timestamps
function convertUIDateToPattern(inputDate, pattern) {
  if (!inputDate) return inputDate;

  const date =
    typeof inputDate === 'object'
      ? inputDate
      : createDate(splitUIDate(inputDate));

  if (date) return format(date, pattern);

  return null;
}

function convertAPIDateToPattern(dateString, pattern) {
  if (!dateString) return dateString;

  const dateParts = dateString.split('-');
  const day = parseInt(dateParts[2], 10);
  const month = parseInt(dateParts[1], 10);
  const year = parseInt(dateParts[0], 10);

  if (validateDate(year, month, day)) {
    return format(createDate({ year, month, day }), pattern);
  }

  return null;
}

export const convertDateToAPIFormat = (date, formatMessage) =>
  convertUIDateToPattern(date, formatMessage(DATE_PATTERN_API));

export const convertDateToUIFormat = (date, formatMessage) =>
  convertAPIDateToPattern(date, formatMessage(DATE_PATTERN_UI));

// for the future implementations - if we need to change timezone offset, now offset is 0
export const adaptDateToLocalTimezone = date => dateFnsAddHours(date, 0);

export const getLastMessageTime = (date, formatMessage) => {
  const adaptedDate = adaptDateToLocalTimezone(date);
  const finalFormat = isSameDay(adaptedDate, new Date())
    ? formatMessage(TIME_PATTERN)
    : `${formatMessage(SHORT_DATE_PATTERN)} ${formatMessage(TIME_PATTERN)}`;

  return format(adaptedDate, finalFormat);
};

export const formatDateTime = (date, formatMessage) => {
  const adaptedDate = adaptDateToLocalTimezone(date);
  const pattern =
    adaptedDate && isSameDay(adaptedDate, new Date())
      ? formatMessage(TIME_PATTERN)
      : `${formatMessage(SHORT_DATE_PATTERN)} ${formatMessage(TIME_PATTERN)}`;

  return adaptedDate && format(adaptedDate, pattern);
};

export const formatDate = (date, formatMessage) => {
  const pattern = formatMessage(SHORT_DATE_PATTERN);
  return format(date, pattern);
};

export const formatDateByYear = (date, formatMessage) => {
  const finalFormat = isSameYear(date, new Date())
    ? formatMessage(SHORT_DATE_PATTERN)
    : formatMessage(FULL_SHORT_DATE_PATTERN);

  return date && format(date, finalFormat);
};

export const formatFullDater = (date, formatMessage) => {
  return date && format(date, formatMessage(FULL_SHORT_DATE_PATTERN));
};

export const formatWithTimezone = (date, timezone, formatMessage) => {
  const finalFormat = isSameYear(date, new Date())
    ? formatMessage(SHORT_DATE_PATTERN)
    : formatMessage(FULL_SHORT_DATE_PATTERN);

  return momentTz(date)
    .tz(timezone)
    .format(finalFormat);
};

export function isNewDayInList(list, index, useDestruction) {
  if (index === 0) return true;
  const previousTime = useDestruction
    ? adaptDateToLocalTimezone(list[index - 1].payload.createdAt)
    : adaptDateToLocalTimezone(list[index - 1].createdAt);
  const nextTime = useDestruction
    ? adaptDateToLocalTimezone(list[index].payload.createdAt)
    : adaptDateToLocalTimezone(list[index].createdAt);
  return !isSameDay(previousTime, nextTime);
}

export const rangeLimitFactory = (pastLimit, futureLimit) => {
  const start = moment()
    .add(pastLimit, 'd')
    .startOf('day');
  const end = moment()
    .add(futureLimit, 'd')
    .endOf('day');

  return function isBetweenLimit(date) {
    return date.isBetween(start, end, null, '[]');
  };
};

export const isBetweenTimeRange = ({
  time,
  startTime,
  endTime,
  formatType,
}) => {
  const pickedTime = moment(time, formatType);
  const beforeTime = moment(startTime, formatType);
  const afterTime = moment(endTime, formatType);

  return pickedTime.isBetween(beforeTime, afterTime);
};

export const yesterday = (d => new Date(d.setDate(d.getDate() - 1)))(
  new Date(),
);

export const isBeforeTime = (start, end) => {
  const mStart = moment(start);
  const mEnd = moment(end);
  return mStart.isBefore(mEnd);
};

export const formatToMonthDayYear = date =>
  date && moment(date).format('MMM DD, YYYY');

// example Aug 03, 2022
export const formatDateToMonthDayYearShort = ({
  date,
  locale = DEFAULT_LOCALE,
}) => {
  const convertedDate = new Date(date);

  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  }).format(convertedDate);
};

export const formatDayMonth = date => {
  if (!date) return null;
  try {
    return format(new Date(date), DAY_MONTH_FORMAT);
  } catch (e) {
    return null;
  }
};

export const formatShortTimeByLocale = ({ date }) => {
  const convertedDate = new Date(date);

  const locale = get(window, 'navigator.language', DEFAULT_LOCALE);

  return new Intl.DateTimeFormat(locale, {
    timeStyle: 'short',
  }).format(convertedDate);
};



// WEBPACK FOOTER //
// ./src/utils/converters/dateConverters.js